// src/pages/student/MyWallet.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
    getMyWallet,
    getStudentOnlineGateways,
    initiateOnlineTopup,
    verifyRazorpayPayment,
    completeOnlineTopup,
    downloadWalletReceipt,
} from "../../api/studentWallet";
import {
  fetchStudentWallet,
  initiateChildWalletTopup,
  verifyChildWalletTopup,
  completeChildWalletTopup,
  downloadChildWalletReceipt,
} from "../../api/studentPortal";
import { usePortalStudentId, useIsParentViewingChild } from "../../hooks/usePortalStudentId";
import ParentPortalHeader from "../../components/parent/ParentPortalHeader";
import { hasSessionContext } from "../../utils/walletDisplay";
import WalletTxnDates from "../../components/common/WalletTxnDates";
import { formatCurrency, formatCurrencyRaw, isCurrencySymbolBefore } from "../../utils/currency";
import { useRegionalSettings } from "../../context/RegionalSettingsContext";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../components/layout/PageLoadingShell";
import { getApiErrorInfo, isUpgradeRequiredError, apiErrorMessage } from "../../utils/apiError";
import { localizeBatchName } from '../../utils/displayNames';
import AutoPayPanel from '../../components/wallet/AutoPayPanel';

const round2 = (n) => formatCurrencyRaw(n);
const fmt = (n) => formatCurrency(n ?? 0);

function txnSessionDetails(txn, t) {
    const parts = [txn.branchName, txn.levelName, localizeBatchName(txn.batchName, t)].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
}

const GATEWAY_LABELS = {
    GATEWAY_RAZORPAY: "Razorpay",
    GATEWAY_STRIPE: "Stripe",
    GATEWAY_PAYPAL: "PayPal",
    GATEWAY_MOLLIE: "Mollie",
    ONLINE_GATEWAY: "Razorpay",
};

/** Mirrors backend OnlinePaymentPurpose.normalize */
function normalizePaymentPurpose(raw) {
    if (!raw || !String(raw).trim()) return "WALLET_TOPUP";
    const p = String(raw).trim().toUpperCase().replace(/-/g, "_");
    if (p === "ENROLLMENT_FEE" || p === "ENROLLMENT") return "ENROLLMENT_FEE";
    return "WALLET_TOPUP";
}

export default function MyWallet() {
    const { t } = useTranslation();
    const childStudentId = usePortalStudentId();
    const isParentView = useIsParentViewingChild();
    const { settings } = useRegionalSettings();
    const [searchParams, setSearchParams] = useSearchParams();

    const paymentPurpose = normalizePaymentPurpose(searchParams.get("purpose"));
    const enrollmentIdParam =
        searchParams.get("enrollmentId") ?? searchParams.get("enrollmentid");

    const [wallet, setWallet] = useState(null);
    const [onlineGateways, setOnlineGateways] = useState([]);
    const [gatewaysLoadError, setGatewaysLoadError] = useState(false);
    const [selectedGateway, setSelectedGateway] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    const [amount, setAmount] = useState("");
    const [redirecting, setRedirecting] = useState(false);
    const [confirmingReturn, setConfirmingReturn] = useState(false);
    const [downloadingTxn, setDownloadingTxn] = useState(null);

    const currencySymbol = settings?.currencySymbol ?? "";
    const symbolBefore = settings?.currencySymbolBefore ?? isCurrencySymbolBefore();
    const currencyCode = settings?.currencyCode || "";

    const load = useCallback(async () => {
        setError("");
        setUpgradeRequired(false);
        try {
            let gatewaysFailed = false;
            const [walletData, gateways] = await Promise.all([
                isParentView ? fetchStudentWallet(childStudentId) : getMyWallet(),
                getStudentOnlineGateways().catch(() => {
                    gatewaysFailed = true;
                    return [];
                }),
            ]);
            setWallet(walletData);
            setOnlineGateways(gateways);
            setGatewaysLoadError(gatewaysFailed);
            setSelectedGateway((prev) => {
                if (prev && gateways.some((g) => g.methodKey === prev)) return prev;
                return gateways[0]?.methodKey || "";
            });
        } catch (e) {
            if (isUpgradeRequiredError(e)) {
                const info = getApiErrorInfo(e, t('pages.wallet.onlineUnavailableDefault'));
                setUpgradeRequired(true);
                setError(info.message);
                setWallet(null);
            } else {
                setWallet(null);
                setError(
                    apiErrorMessage(e, t('pages.wallet.loadFailed'))
                );
            }
        } finally {
            setLoading(false);
        }
    }, [isParentView, childStudentId, t]);

    useEffect(() => {
        load();
    }, [load]);

    const handleReturnFromGateway = useCallback(async () => {
        const topup = searchParams.get("topup");
        const gatewayParam = searchParams.get("gateway");
        if (topup !== "success" || !gatewayParam) return;

        const gateway =
            gatewayParam === "STRIPE" ? "GATEWAY_STRIPE"
                : gatewayParam === "PAYPAL" ? "GATEWAY_PAYPAL"
                    : gatewayParam === "MOLLIE" ? "GATEWAY_MOLLIE"
                        : null;
        if (!gateway) return;

        const externalId =
            gateway === "GATEWAY_STRIPE" || gateway === "GATEWAY_MOLLIE"
                ? (searchParams.get("session_id") || sessionStorage.getItem("pending_gateway_session_id"))
                : searchParams.get("token");
        if (!externalId) return;
        sessionStorage.removeItem("pending_gateway_session_id");

        setConfirmingReturn(true);
        setError("");
        try {
            const updated = isParentView
                ? await completeChildWalletTopup(childStudentId, gateway, externalId)
                : await completeOnlineTopup(gateway, externalId);
            setWallet(updated);
            setSearchParams({}, { replace: true });
        } catch (e) {
            setError(apiErrorMessage(e, t('pages.wallet.paymentConfirmFailed')));
        } finally {
            setConfirmingReturn(false);
        }
    }, [searchParams, setSearchParams, isParentView, childStudentId, t]);

    useEffect(() => {
        handleReturnFromGateway();
    }, [handleReturnFromGateway]);

    const handlePayNow = async () => {
        const amountVal = parseFloat(amount);
        if (!amountVal || amountVal < 1) {
            setError(t('pages.wallet.invalidAmount', { symbol: currencySymbol }));
            return;
        }
        if (!selectedGateway) {
            setError(t('pages.wallet.selectPaymentMethod'));
            return;
        }
        if (paymentPurpose === "ENROLLMENT_FEE") {
            const eid = enrollmentIdParam ? Number(enrollmentIdParam) : NaN;
            if (!enrollmentIdParam || Number.isNaN(eid) || eid <= 0) {
                setError(t('pages.wallet.invalidEnrollmentLink'));
                return;
            }
        }

        setRedirecting(true);
        setError("");

        try {
            const payOptions = {
                purpose: paymentPurpose === "ENROLLMENT_FEE" ? "ENROLLMENT_FEE" : undefined,
                enrollmentId:
                    paymentPurpose === "ENROLLMENT_FEE" && enrollmentIdParam
                        ? Number(enrollmentIdParam)
                        : undefined,
            };
            const orderApi = isParentView
                ? () => initiateChildWalletTopup(childStudentId, amountVal, selectedGateway, payOptions)
                : () => initiateOnlineTopup(amountVal, selectedGateway, payOptions);
            const order = await orderApi();

            if (order.provider === "RAZORPAY" || selectedGateway === "GATEWAY_RAZORPAY") {
                await handleRazorpayCheckout(order, amountVal);
            } else if (order.provider === "STRIPE" && order.checkoutUrl) {
                if (order.sessionId) sessionStorage.setItem("pending_gateway_session_id", order.sessionId);
                window.location.href = order.checkoutUrl;
            } else if (order.provider === "MOLLIE" && order.checkoutUrl) {
                if (order.sessionId) sessionStorage.setItem("pending_gateway_session_id", order.sessionId);
                window.location.href = order.checkoutUrl;
            } else if (order.provider === "PAYPAL" && order.approvalUrl) {
                window.location.href = order.approvalUrl;
            } else {
                throw new Error(t('pages.wallet.unsupportedGateway', { defaultValue: 'Unsupported payment gateway response.' }));
            }
        } catch (e) {
            if (isUpgradeRequiredError(e)) {
                const info = getApiErrorInfo(e, t('pages.wallet.paymentUpgradeRequired'));
                setUpgradeRequired(true);
                setError(info.message);
            } else {
                setError(
                    apiErrorMessage(e, t('pages.wallet.paymentStartFailed'))
                );
            }
            setRedirecting(false);
        }
    };

    const handleRazorpayCheckout = async (order, amountVal) => {
        const { keyId, orderId, amountMinor, currency } = order;

        if (!window.Razorpay) {
            await new Promise((resolve, reject) => {
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = resolve;
                script.onerror = reject;
                document.body.appendChild(script);
            });
        }

        const rzp = new window.Razorpay({
            key: keyId,
            amount: amountMinor,
            currency: currency || currencyCode || undefined,
            order_id: orderId,
            name: t('pages.wallet.razorpayTitle'),
            description: t('pages.wallet.razorpayDesc'),
            theme: { color: "#4f46e5" },
            handler: async (response) => {
                try {
                    const verifyPayload = {
                        razorpayPaymentId: response.razorpay_payment_id,
                        razorpayOrderId: response.razorpay_order_id,
                        razorpaySignature: response.razorpay_signature,
                        amount: amountVal,
                    };
                    const updated = isParentView
                        ? await verifyChildWalletTopup(childStudentId, verifyPayload)
                        : await verifyRazorpayPayment(verifyPayload);
                    setWallet(updated);
                    setAmount("");
                } catch (e) {
                    setError(apiErrorMessage(e, t('pages.wallet.paymentVerifyFailed')));
                } finally {
                    setRedirecting(false);
                }
            },
            modal: {
                ondismiss: () => setRedirecting(false),
            },
        });
        rzp.open();
    };

    const handleDownloadReceipt = async (txnId) => {
        setDownloadingTxn(txnId);
        try {
            if (isParentView) {
                await downloadChildWalletReceipt(childStudentId, txnId);
            } else {
                await downloadWalletReceipt(txnId);
            }
        } catch {
            setError(t('pages.wallet.receiptDownloadFailed'));
        } finally {
            setDownloadingTxn(null);
        }
    };

    const txnColor = (type) =>
        type === "CREDIT"
            ? "text-green-600 font-semibold"
            : "text-red-500 font-semibold";

    const txnSign = (type) => (type === "CREDIT" ? "+" : "−");

    const txnLabel = (reason) => {
        const map = {
            ADVANCE_PAYMENT: t('pages.wallet.txnAdvanceTopup'),
            ONLINE_TOPUP: t('pages.wallet.txnOnlineTopup'),
            ENROLLMENT_FEE_PAYMENT: t('pages.wallet.txnEnrollmentPayment'),
            SESSION_CHARGE: t('pages.wallet.txnSessionCharge'),
            SESSION_REVERSAL: t('pages.wallet.txnSessionReversal'),
            PERIOD_CHARGE: t('pages.wallet.txnPeriodCharge', { defaultValue: 'Period charge' }),
            BANK_DEBIT: t('pages.wallet.txnBankDebit', { defaultValue: 'Bank auto-pay' }),
            DISCOUNT: t('pages.wallet.txnDiscount'),
            REFUND: t('pages.wallet.txnRefund'),
            APPLY: t('pages.wallet.txnApplied'),
        };
        return map[reason] || (reason ? reason.replace(/_/g, " ") : t('pages.wallet.txnDefault'));
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell"
                title={isParentView ? t('pages.wallet.childTitle') : t('pages.wallet.title')}
                subtitle={isParentView
                    ? t('pages.wallet.childSubtitle')
                    : t('pages.wallet.subtitle')}
                showBack={false}
            />
        );
    }

    const balance = wallet?.balance;
    const transactions = wallet?.transactions
        ? [...wallet.transactions].sort(
            (a, b) => new Date(b.created) - new Date(a.created)
        )
        : [];

    const gatewayAvailable = onlineGateways.length > 0;
    const selectedLabel = GATEWAY_LABELS[selectedGateway] || selectedGateway;

    if (!loading && !wallet && error && !upgradeRequired) {
        return (
            <div className="mx-auto max-w-2xl space-y-6 p-4">
                <ParentPortalHeader />
                <PageHeaderSection
                    title={isParentView ? t('pages.wallet.childTitle') : t('pages.wallet.title')}
                    subtitle={isParentView
                        ? t('pages.wallet.childSubtitle')
                        : t('pages.wallet.subtitle')}
                />
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status">
                    {error}
                </div>
                <button
                    type="button"
                    onClick={() => { setLoading(true); load(); }}
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                    {t('common.retry', { defaultValue: 'Retry' })}
                </button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-2xl space-y-6 p-4">
            <ParentPortalHeader />
            <PageHeaderSection
                title={isParentView ? t('pages.wallet.childTitle') : t('pages.wallet.title')}
                subtitle={isParentView
                    ? t('pages.wallet.childSubtitle')
                    : t('pages.wallet.subtitle')}
            />

            <div className="rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-600 p-6 text-white shadow-xl">
                <p className="text-sm font-medium opacity-80">{t('pages.wallet.balanceLabel')}</p>
                <p className="mt-1 text-4xl font-bold tracking-tight">{balance == null ? '—' : fmt(balance)}</p>
                <p className="mt-1 text-xs opacity-60">
                    {t('pages.wallet.balanceHint')}
                </p>
            </div>

            {upgradeRequired && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-semibold">{t('pages.wallet.onlineUnavailableTitle')}</p>
                    <p className="mt-1">{error || t('pages.wallet.onlineUnavailableDefault')}</p>
                </div>
            )}

            {error && !upgradeRequired && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {paymentPurpose === "ENROLLMENT_FEE" && enrollmentIdParam && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                    {t('pages.wallet.enrollmentPaying', { id: enrollmentIdParam })}
                </div>
            )}

            <AutoPayPanel isParentView={isParentView} childStudentId={childStudentId} />

            {confirmingReturn && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800 flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                    {t('pages.wallet.confirmingPayment')}
                </div>
            )}

            {gatewayAvailable && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h2 className="mb-1 text-base font-semibold text-gray-900">
                        {t('pages.wallet.addMoney')}
                    </h2>
                    <p className="mb-4 text-xs text-gray-500">
                        {t('pages.wallet.addMoneyHint')}
                    </p>

                    {onlineGateways.length > 1 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                            {onlineGateways.map((g) => (
                                <button
                                    key={g.methodKey}
                                    type="button"
                                    onClick={() => setSelectedGateway(g.methodKey)}
                                    disabled={redirecting || confirmingReturn}
                                    className={`rounded-full px-3 py-1.5 text-xs font-medium border transition ${
                                        selectedGateway === g.methodKey
                                            ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                            : "border-gray-200 text-gray-600 hover:border-indigo-200"
                                    }`}
                                >
                                    {GATEWAY_LABELS[g.methodKey] || g.displayName || g.methodKey}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            {symbolBefore && currencySymbol && (
                            <span className="absolute inset-y-0 left-3 flex items-center text-sm font-medium text-gray-500">
                                {currencySymbol}
                            </span>
                            )}
                            <input
                                type="number"
                                min="1"
                                step="1"
                                className={`w-full rounded-xl border border-gray-300 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
                                  symbolBefore ? "pl-7 pr-3" : "pl-3 pr-7"
                                }`}
                                placeholder={t('pages.wallet.enterAmount')}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handlePayNow()}
                                disabled={redirecting || confirmingReturn}
                            />
                            {!symbolBefore && currencySymbol && (
                            <span className="absolute inset-y-0 right-3 flex items-center text-sm font-medium text-gray-500">
                                {currencySymbol}
                            </span>
                            )}
                        </div>
                        <button
                            onClick={handlePayNow}
                            disabled={redirecting || confirmingReturn || !amount}
                            className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {redirecting ? (
                                <>
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    {t('pages.wallet.redirecting')}
                                </>
                            ) : (
                                <>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                    {t('pages.wallet.payNow')}
                                </>
                            )}
                        </button>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                        {[10, 25, 50, 100, 200].map((q) => (
                            <button
                                key={q}
                                onClick={() => setAmount(String(q))}
                                disabled={redirecting || confirmingReturn}
                                className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
                            >
                                {fmt(q)}
                            </button>
                        ))}
                    </div>

                    <p className="mt-4 text-xs text-gray-400">
                        {t('pages.wallet.securePayments', { gateway: selectedLabel })}
                    </p>
                </div>
            )}

            {!gatewayAvailable && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
                    {gatewaysLoadError
                        ? t('pages.wallet.gatewaysLoadFailed')
                        : t('pages.wallet.topupUnavailable')}
                </div>
            )}

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 py-3">
                    <h2 className="text-base font-semibold text-gray-900">
                        {t('pages.wallet.transactionHistory')}
                    </h2>
                </div>

                {transactions.length === 0 ? (
                    <p className="px-5 py-8 text-center text-sm text-gray-500">
                        {wallet
                            ? t('pages.wallet.noTransactions')
                            : t('pages.wallet.loadFailed')}
                    </p>
                ) : (
                    <ul className="divide-y divide-gray-50">
                        {transactions.map((txn) => (
                            <li key={txn.txnId} className="flex items-start justify-between px-5 py-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-800">
                                        {txnLabel(txn.reason)}
                                    </p>
                                    <div className="mt-1">
                                        <WalletTxnDates txn={txn} compact />
                                    </div>
                                    {!hasSessionContext(txn) && txn.reference && (
                                        <p className="mt-0.5 text-xs text-gray-400">
                                            {t('pages.wallet.ref')}: {txn.reference}
                                        </p>
                                    )}
                                    {hasSessionContext(txn) && txnSessionDetails(txn, t) && (
                                        <p className="mt-0.5 text-xs text-gray-500">
                                            {txnSessionDetails(txn, t)}
                                        </p>
                                    )}
                                </div>
                                <div className="ml-4 flex flex-shrink-0 items-center gap-2">
                                    <span className={`text-sm ${txnColor(txn.txnType)}`}>
                                        {txnSign(txn.txnType)}
                                        {fmt(round2(txn.amount))}
                                    </span>
                                    {txn.txnType === "CREDIT" && (
                                        <button
                                            title={t('pages.wallet.downloadReceipt')}
                                            onClick={() => handleDownloadReceipt(txn.txnId)}
                                            disabled={downloadingTxn === txn.txnId}
                                            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-indigo-600 disabled:opacity-40"
                                        >
                                            {downloadingTxn === txn.txnId ? (
                                                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                </svg>
                                            ) : (
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
